import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Col, Empty, Row, Skeleton, Space, Switch, Tag } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Inbox, Save } from 'lucide-react';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { formsApi } from '@api/forms';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import type { FieldType } from '../constants';
import type { FormDetail, FormFieldModel } from '../types';
import { createField, serializeFields, validateFields } from '../utils/fields';
import { FormMetaPanel, type FormMetaValues } from '../components/FormMetaPanel';
import { FieldPalette } from '../components/FieldPalette';
import { FieldList } from '../components/FieldList';
import { FieldPropertiesPanel } from '../components/FieldPropertiesPanel';
import { FormPreview } from '../components/FormPreview';

interface EditorDraft extends FormMetaValues {
  fields: FormFieldModel[];
}

function toDraft(form: FormDetail): EditorDraft {
  return {
    title: form.title,
    slug: form.slug,
    formType: form.formType,
    emailRecipients: form.emailRecipients,
    kvkkText: form.kvkkText,
    captchaEnabled: form.captchaEnabled,
    fields: form.fields,
  };
}

function signature(draft: EditorDraft): string {
  return JSON.stringify({
    title: draft.title.trim(),
    slug: draft.slug.trim(),
    formType: draft.formType,
    emailRecipients: draft.emailRecipients,
    kvkkText: draft.kvkkText,
    captchaEnabled: draft.captchaEnabled,
    fields: serializeFields(draft.fields),
  });
}

export function FormEditorPage() {
  const { t } = useTranslation(['forms', 'common']);
  const td = t as unknown as (key: string, opts?: Record<string, unknown>) => string;
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = useCan(PERMISSIONS.FORM_MANAGE);
  const canSubmissions = useCan(PERMISSIONS.FORM_SUBMISSIONS);

  const { data: form, isLoading } = useQuery({
    queryKey: ['form', id],
    queryFn: () => formsApi.get(id),
    enabled: !!id,
  });

  const [draft, setDraft] = useState<EditorDraft | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const openedRef = useRef<string | null>(null);

  // Formu ilk yüklendiğinde (veya id değişince) draft'a al; publish/refetch draft'ı ezmez.
  useEffect(() => {
    if (form && openedRef.current !== form.id) {
      openedRef.current = form.id;
      setDraft(toDraft(form));
      setSelectedId(null);
    }
  }, [form]);

  const validationErrors = useMemo(
    () => (draft ? validateFields(draft.fields, draft.formType) : []),
    [draft],
  );
  const invalidFieldIds = useMemo(
    () => new Set(validationErrors.map((e) => e.fieldId).filter((v): v is string => v !== null)),
    [validationErrors],
  );
  const errorMessages = useMemo(() => {
    const tr = t as unknown as (key: string, opts?: Record<string, unknown>) => string;
    return Array.from(
      new Set(
        validationErrors.map((e) =>
          e.code === 'keyDuplicate'
            ? tr('builder.errors.keyDuplicate', { key: e.key })
            : tr(`builder.errors.${e.code}`),
        ),
      ),
    );
  }, [validationErrors, t]);

  const isDirty = !!draft && !!form && signature(draft) !== signature(toDraft(form));
  const canSave =
    canManage && !!draft && draft.title.trim().length > 0 && validationErrors.length === 0 && isDirty;

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('no draft');
      return formsApi.update(id, {
        title: draft.title.trim(),
        formType: draft.formType,
        slug: draft.slug.trim(),
        fields: draft.fields,
        emailRecipients: draft.emailRecipients,
        kvkkText: draft.kvkkText,
        captchaEnabled: draft.captchaEnabled,
      });
    },
    onSuccess: async (saved) => {
      openedRef.current = '';
      setDraft(toDraft(saved));
      setSelectedId(null);
      toast.success(t('editor.saved'));
      await qc.invalidateQueries({ queryKey: ['form', id] });
      await qc.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const publishMutation = useMutation({
    mutationFn: () => formsApi.setPublished(id, form?.status !== 'Published'),
    onSuccess: async () => {
      toast.success(form?.status === 'Published' ? t('toast.unpublished') : t('toast.published'));
      await qc.invalidateQueries({ queryKey: ['form', id] });
      await qc.invalidateQueries({ queryKey: ['forms'] });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateMeta = (patch: Partial<FormMetaValues>) =>
    setDraft((d) => (d ? { ...d, ...patch } : d));

  const addField = (type: FieldType) => {
    const field = createField(type, { label: td(`builder.fieldType.${type}`) });
    setDraft((d) => (d ? { ...d, fields: [...d.fields, field] } : d));
    setSelectedId(field.id);
  };
  const updateField = (fieldId: string, patch: Partial<FormFieldModel>) =>
    setDraft((d) =>
      d ? { ...d, fields: d.fields.map((f) => (f.id === fieldId ? { ...f, ...patch } : f)) } : d,
    );
  const removeField = (fieldId: string) => {
    setDraft((d) => (d ? { ...d, fields: d.fields.filter((f) => f.id !== fieldId) } : d));
    setSelectedId((cur) => (cur === fieldId ? null : cur));
  };
  const reorderFields = (next: FormFieldModel[]) =>
    setDraft((d) => (d ? { ...d, fields: next } : d));

  const selectedField = draft?.fields.find((f) => f.id === selectedId) ?? null;

  if (isLoading || (!draft && form)) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  if (!form || !draft) {
    return (
      <Card>
        <Empty description={t('editor.notFound')}>
          <Button onClick={() => void navigate(ROUTES.FORMS)}>{t('editor.back')}</Button>
        </Empty>
      </Card>
    );
  }

  return (
    <div data-testid="form-editor">
      <PageHeader
        title={form.title || t('title')}
        description={t('description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('title'), to: ROUTES.FORMS },
          { label: form.title || '…' },
        ]}
        actions={
          <Space wrap>
            <Button icon={<ArrowLeft size={15} />} onClick={() => void navigate(ROUTES.FORMS)}>
              {t('editor.back')}
            </Button>
            {canSubmissions ? (
              <Button
                icon={<Inbox size={15} />}
                onClick={() => void navigate(ROUTES.FORM_SUBMISSIONS.replace(':id', id))}
              >
                {t('editor.submissions')}
              </Button>
            ) : null}
            <Space size={6}>
              <Switch
                checked={form.status === 'Published'}
                disabled={!canManage || publishMutation.isPending}
                onChange={() => publishMutation.mutate()}
                aria-label={t('list.columns.status')}
                data-testid="form-publish-toggle"
              />
              <Tag color={form.status === 'Published' ? 'green' : 'default'}>
                {form.status === 'Published' ? t('status.Published') : t('status.Draft')}
              </Tag>
            </Space>
            <Button
              type="primary"
              icon={<Save size={15} />}
              disabled={!canSave}
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              data-testid="form-save"
            >
              {t('editor.save')}
            </Button>
          </Space>
        }
      />

      {errorMessages.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('builder.errors.title')}
          description={
            <ul style={{ margin: 0, paddingInlineStart: 18 }}>
              {errorMessages.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          }
          data-testid="form-errors"
        />
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card title={t('editor.meta')} size="small">
            <FormMetaPanel values={draft} onChange={updateMeta} disabled={!canManage} />
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card title={t('editor.designer')} size="small">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={7}>
                <FieldPalette formType={draft.formType} onAdd={addField} disabled={!canManage} />
              </Col>
              <Col xs={24} md={9}>
                <FieldList
                  fields={draft.fields}
                  selectedId={selectedId}
                  invalidIds={invalidFieldIds}
                  onSelect={setSelectedId}
                  onReorder={reorderFields}
                  onRemove={removeField}
                  disabled={!canManage}
                />
              </Col>
              <Col xs={24} md={8}>
                <FieldPropertiesPanel
                  field={selectedField}
                  onChange={(patch) => selectedField && updateField(selectedField.id, patch)}
                  disabled={!canManage}
                />
              </Col>
            </Row>
          </Card>

          <Card title={t('editor.preview')} size="small" style={{ marginTop: 16 }}>
            <FormPreview fields={draft.fields} kvkkText={draft.kvkkText} />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
